/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-13 14:14:35
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import type { SubscriptionState, SubscriptionAction } from 'stores/flow-type/subscription.type';

import {
  VERIFY_DISCOUNT_CODE_REQUEST,
  VERIFY_DISCOUNT_CODE_SUCCESS,
  VERIFY_DISCOUNT_CODE_FAILURE,
  SUBSCRIBE_PLAN_REQUEST,
  SUBSCRIBE_PLAN_SUCCESS,
  SUBSCRIBE_PLAN_FAILURE,
  ACTIVATE_CHARGE_REQUEST,
  ACTIVATE_CHARGE_SUCCESS,
  ACTIVATE_CHARGE_FAILURE,
  CLEAR_DISCOUNT_CODE,
  SUBSCRIBE_ADDON_REQUEST,
  SUBSCRIBE_ADDON_SUCCESS,
  SUBSCRIBE_ADDON_FAILURE,
  UNSUBSCRIBE_ADDON_REQUEST,
  UNSUBSCRIBE_ADDON_SUCCESS,
  UNSUBSCRIBE_ADDON_FAILURE,
  GET_ADDONS_REQUEST,
  GET_ADDONS_SUCCESS,
  GET_ADDONS_FAILURE,
  GET_ADDON_STATUS_REQUEST,
  GET_ADDON_STATUS_SUCCESS,
  GET_ADDON_STATUS_FAILURE,
  GET_PACKAGE_PLANS_REQUEST,
  GET_PACKAGE_PLANS_SUCCESS,
  GET_PACKAGE_PLANS_FAILURE,
  GET_INVOICE_PLANS_REQUEST,
  GET_INVOICE_PLANS_SUCCESS,
  GET_INVOICE_PLANS_FAILURE
} from 'stores/flow-type/subscription-v3.type';

const initialState: SubscriptionState = {
  verifying: false,
  subscribing: false,
  loading: false,
  addonSubscribing: false,
  unsubscribing: false,
  activating: false,
  subscribeSuccess: false,
  unsubscribeSuccess: false,
  addonSubscribeSuccess: false,
  success: false,
  error: undefined,
  remainCycles: 0,
  addonSubscribed: {},
  addons: [],
  termsConditions: '',
  confirmationUrl: undefined,
  migrateESCluster: false,
  packagePlans: { basic: [], essential: [], professional: [] },
  getPackageSuccess: false,
  gettingPackage: false,
  msgError: null,
  getAddonSuccess: false,
  gettingInvoice: false,
  getInvoiceSuccess: false,
  invoice: {},
  addonStatus: {}
};

const subscriptionReducers = (
  state: SubscriptionState = initialState,
  action: SubscriptionAction
) => {
  switch (action.type) {
    // Verify discount code
    case VERIFY_DISCOUNT_CODE_REQUEST:
      return { ...state, verifying: true };
    case VERIFY_DISCOUNT_CODE_SUCCESS: {
      const { data = {}, subscribeOn } = action.payload;
      const discount = _.get(data, 'discount', {});
      const remainCycles = _.get(data, 'remainCycles', {});
      const termsConditions = _.get(data, 'termsConditions', '');

      if (subscribeOn === 'add-on-page') {
        return {
          ...state,
          verifying: false,
          error: undefined,
          termsConditions,
          discountAddon: discount,
          remainCycles,
          msgVerifyError: null
        };
      }
      return {
        ...state,
        verifying: false,
        error: undefined,
        termsConditions,
        discount: discount,
        remainCycles,
        msgVerifyError: null
      };
    }
    case VERIFY_DISCOUNT_CODE_FAILURE: {
      const data = _.get(action, 'payload.data', {});
      const responseMsg = _.get(data, 'message', '');

      const splitMsg = responseMsg.split(':');
      const msg = _.get(splitMsg, '[1]', responseMsg);
      return {
        ...state,
        verifying: false,
        success: false,
        error: action.payload,
        msgError: msg.trimStart()
      };
    }

    // Clear discount code
    case CLEAR_DISCOUNT_CODE:
      return {
        ...state,
        discount: {},
        discountAddon: {},
        msgError: null,
        error: undefined
      };

    // Subscribe a plan
    case SUBSCRIBE_PLAN_REQUEST:
      return { ...state, subscribing: true, subscribeSuccess: false };
    case SUBSCRIBE_PLAN_SUCCESS: {
      const { data } = action.payload;
      const { confirmationUrl } = data;
      return {
        ...state,
        subscribing: false,
        subscribeSuccess: true,
        error: undefined,
        confirmationUrl
      };
    }
    case SUBSCRIBE_PLAN_FAILURE:
      return {
        ...state,
        subscribeSuccess: false,
        subscribing: false,
        success: false,
        error: action.payload
      };

    // Activate charge
    case ACTIVATE_CHARGE_REQUEST:
      return { ...state, activating: true, success: false };
    case ACTIVATE_CHARGE_SUCCESS: {
      const { data } = action.payload;

      const { message, migrateESCluster = false } = data;

      if (message === 'success') {
        return { ...state, activating: false, error: undefined, success: true, migrateESCluster };
      }

      return { ...state, activating: false, error: undefined, success: false, migrateESCluster };
    }
    case ACTIVATE_CHARGE_FAILURE:
      return {
        ...state,
        activating: false,
        success: false,
        error: action.payload
      };
    // ADDONS

    // Subscribe an addon
    case SUBSCRIBE_ADDON_REQUEST:
      return { ...state, addonSubscribing: true, addonSubscribeSuccess: false };
    case SUBSCRIBE_ADDON_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        addonSubscribing: false,
        addonSubscribeSuccess: true,
        error: undefined,
        addonSubscribed: data
      };
    }
    case SUBSCRIBE_ADDON_FAILURE:
      return {
        ...state,
        addonSubscribeSuccess: false,
        addonSubscribing: false,
        success: false,
        error: action.payload
      };
    // Unsubscribe an addon
    case UNSUBSCRIBE_ADDON_REQUEST:
      return { ...state, unsubscribing: true, unsubscribeSuccess: false };
    case UNSUBSCRIBE_ADDON_SUCCESS: {
      return {
        ...state,
        unsubscribing: false,
        unsubscribeSuccess: true,
        error: undefined
      };
    }
    case UNSUBSCRIBE_ADDON_FAILURE:
      return {
        ...state,
        unsubscribeSuccess: false,
        unsubscribing: false,
        success: false,
        error: action.payload
      };
    // Get all list addon
    case GET_ADDONS_REQUEST:
      return { ...state, loading: true, getAddonSuccess: false };
    case GET_ADDONS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        loading: false,
        getAddonSuccess: true,
        error: undefined,
        addons: data
      };
    }
    case GET_ADDONS_FAILURE:
      return {
        ...state,
        loading: false,
        getAddonSuccess: false,
        error: action.payload
      };
    // Get current status addon
    case GET_ADDON_STATUS_REQUEST:
      return { ...state, loading: true, success: false };
    case GET_ADDON_STATUS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        addonStatus: data
      };
    }
    case GET_ADDON_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    // Get all list package
    case GET_PACKAGE_PLANS_REQUEST:
      return { ...state, gettingPackage: true, getPackageSuccess: false };
    case GET_PACKAGE_PLANS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        gettingPackage: false,
        getPackageSuccess: true,
        error: undefined,
        packagePlans: data
      };
    }
    case GET_PACKAGE_PLANS_FAILURE:
      return {
        ...state,
        gettingPackage: false,
        getPackageSuccess: false,
        error: action.payload
      };
    // Get detail invoice
    case GET_INVOICE_PLANS_REQUEST:
      return { ...state, gettingInvoice: true, getInvoiceSuccess: false };
    case GET_INVOICE_PLANS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        gettingInvoice: false,
        getInvoiceSuccess: true,
        error: undefined,
        invoice: data
      };
    }
    case GET_INVOICE_PLANS_FAILURE:
      return {
        ...state,
        gettingInvoice: false,
        getInvoiceSuccess: false,
        error: action.payload
      };
    default:
      return state;
  }
};
export default subscriptionReducers;
